import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BasePage from '../../components/Layout/BasePage';
import MasterDetailLayout from '../../components/Layout/MasterDetailLayout';
import { renderTabLabel } from '../../components/Layout/tabLabel';
import useUrlTab from '../../hooks/useUrlTab';
import Products from './Products';
import ProductsOverview from './ProductsOverview';
import BOMs from '../BOM/BOMs';
import ProductTypes from '../ProductTypes/ProductTypes';
import NewProductWizard from './NewProductWizard';

function ProductsPage() {
  const { t } = useTranslation('products');
  // Active tab in the URL (?tab=), remembered per module
  const { activeTab, setActiveTab } = useUrlTab(
    'products', ['overview', 'products', 'bom', 'product-types'], 'overview'
  );
  const [bomToolbar, setBomToolbar] = useState(null);
  const [bomViewMode, setBomViewMode] = useState('list');
  const [selectedBomId, setSelectedBomId] = useState(null);
  const [bomProductId, setBomProductId] = useState(null);
  const [showWizard, setShowWizard] = useState(false);


  const createTabLabel = (translationKey, shortcutLetter) => renderTabLabel(t(translationKey), shortcutLetter);

  const tabs = useMemo(() => [
    {
      id: 'overview',
      label: createTabLabel('tabs.mainOverview', 'o'),
      shortcut: 'o',
      hasSubTabs: false
    },
    {
      id: 'products',
      label: createTabLabel('tabs.mainProducts', 'p'),
      shortcut: 'p',
      hasSubTabs: false
    },
    {
      id: 'bom',
      label: createTabLabel('tabs.mainBom', 'b'),
      shortcut: 'b',
      hasSubTabs: false
    },
    {
      id: 'product-types',
      label: createTabLabel('tabs.mainProductTypes', 't'),
      shortcut: 't',
      hasSubTabs: false
    }
  ], [t, createTabLabel]);

  const handleNavigateToBOM = (bomId, mode) => {
    setActiveTab('bom');
    setSelectedBomId(bomId);
    setBomViewMode(mode);
  };


  // Expose toolbar setters immediately
  React.useEffect(() => {
    window.setBOMToolbar = setBomToolbar;

    return () => {
      delete window.setBOMToolbar;
    };
  }, []);



  // Configuration object for MasterDetailLayout
  const config = {
    tabs,
    entities: {},
    toolbars: {
      overview: null, // No toolbar for overview dashboard
      products: null,
      bom: typeof bomToolbar === 'function' ? bomToolbar() : bomToolbar,
      'product-types': null // ProductTypes component handles its own toolbar
    }
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="tab-content with-padding">
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <button
                className="btn btn-primary"
                style={{ padding: '0.55rem 1.1rem', fontWeight: 600, fontSize: '0.875rem' }}
                onClick={() => setShowWizard(true)}
              >
                + {t('wizard.launchButton', 'New Product Setup')}
              </button>
            </div>
            <ProductsOverview />
          </div>
        );

      case 'products':
        return <Products />;

      case 'bom':
        return (
          <BOMs
            initialViewMode={bomViewMode}
            initialBOMId={selectedBomId}
            initialProductId={bomProductId}
          />
        );

      case 'product-types':
        return <ProductTypes />;

      default:
        return null;
    }
  };

  return (
    <BasePage title={t('page.title')} className="products-page">
      <MasterDetailLayout
        config={config}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {renderTabContent()}
      </MasterDetailLayout>
      <NewProductWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onProductCreated={() => {
          // Switching to products tab will trigger a refresh naturally
          setActiveTab('products');
        }}
      />
    </BasePage>
  );
}

export default ProductsPage;